import { ComponentChildren, Ref, RefObject } from 'preact';
import { useRef } from 'preact/hooks';

import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { useFormContext } from '../hooks/UNSAFE_useFormContext';
import { useFormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { Flex } from '../UNSAFE_Flex';
import { classNames } from '../utils/UNSAFE_classNames';
import { useInputGroupContext } from '../hooks/UNSAFE_useInputGroupContext';
import { useTextFieldTooltip } from './useTextFieldTooltip';
import { useUser } from '../hooks/UNSAFE_useUser';
import type { TextFieldStyles, TextFieldVariantOptions } from './themes/TextFieldStyles.css';
import { TextFieldRedwoodTheme } from './themes/redwood/TextFieldTheme';
import type {
  ReadonlyTextFieldStyles,
  ReadonlyTextFieldVariantOptions
} from './themes/ReadonlyTextFieldStyles.css';
import { ReadonlyTextFieldRedwoodTheme } from './themes/redwood/ReadonlyTextFieldTheme';

type VariantProps =
  | {
      variant?: never;
      resize?: never;
    }
  | {
      variant: 'textarea';
      resize?: 'horizontal' | 'vertical' | 'both';
    };

type Props = VariantProps & {
  mainContent?: ComponentChildren;
  insideLabel?: ComponentChildren;
  endContent?: ComponentChildren;
  rootRef?: Ref<HTMLDivElement>;
  startContent?: ComponentChildren;
  hasZeroStartMargin?: boolean;
  labelText?: string;
};

// Renders the oj-text-field-container dom which includes the
// main (where the inside label and inputElem goes),
// and end pieces of the form component.
// This does not include user assistance because that is rendered outside the
// oj-text-field-container; that is rendered in the TextField component.
export const ReadonlyTextFieldContent = ({
  variant,
  insideLabel,
  labelText,
  mainContent,
  endContent,
  rootRef,
  resize,
  startContent
}: Props) => {
  const { isFormLayout, isReadonly: isReadonlyForm } = useFormContext();
  const { hasTruncatedLabel, isLoading } = useFormFieldContext();
  const { direction } = useUser();
  const isLtr = direction === 'ltr';
  const isInsideNonReadonlyForm = isFormLayout && !isReadonlyForm;
  const hasInsideLabel = insideLabel !== undefined;
  const inputGroupContext = useInputGroupContext();
  const hasGroupContext = inputGroupContext !== null;
  // depending on browser direction, apply the appropriate styling.
  // when 'rtl' the flex row direction is reversed.
  const inputGroupPosition = inputGroupContext
    ? inputGroupContext.last
      ? isLtr
        ? 'groupRight'
        : 'groupLeft'
      : inputGroupContext.index === 0
      ? isLtr
        ? 'groupLeft'
        : 'groupRight'
      : 'groupMiddle'
    : undefined;

  const {
    variantClasses: middleStyles,
    styles: { textFieldEndContent }
  } = useComponentTheme<TextFieldVariantOptions, TextFieldStyles>(TextFieldRedwoodTheme, {
    middleContent: variant,
    textAreaResizeDirection: resize && variant === 'textarea' ? direction : undefined
  });

  const {
    classes: readonlyClassNames,
    styles: {
      readonlyTextFieldMiddleBase,
      readonlyTextFieldMiddleNotInEnabledForm,
      readonlyTextFieldMiddleInEnabledForm,
      readonlyTextFieldMiddleInEnabledFormNotTextarea,
      startContentInsideLabel,
      startContentMarginEnd,
      startContentMarginStart,
      startContentStartTopLabel
    }
  } = useComponentTheme<ReadonlyTextFieldVariantOptions, ReadonlyTextFieldStyles>(
    ReadonlyTextFieldRedwoodTheme,
    {
      insideNonReadonlyForm: isInsideNonReadonlyForm
        ? 'isInsideNonReadonlyForm'
        : 'notInsideNonReadonlyForm',
      insideLabel: hasInsideLabel ? 'hasInsideLabel' : 'noInsideLabel',
      textarea: variant === 'textarea' ? 'isTextArea' : 'notTextArea',
      loading: isLoading ? 'isLoading' : 'notLoading',
      withinGroup: hasGroupContext ? 'isWithinGroup' : undefined,
      inputGroupPosition,
      resize: resize ?? 'none'
    }
  );

  // JET-73911: Temporarily use style classes directly instead of getting
  // startContent styles from theming.
  const readonlyTextFieldStartContentStyles = classNames([
    hasInsideLabel ? startContentInsideLabel : startContentStartTopLabel,
    isInsideNonReadonlyForm ? startContentMarginStart : startContentMarginEnd
  ]);

  const middleClasses = classNames([
    middleStyles,
    readonlyTextFieldMiddleBase,
    isInsideNonReadonlyForm && readonlyTextFieldMiddleInEnabledForm,
    isInsideNonReadonlyForm &&
      variant !== 'textarea' &&
      readonlyTextFieldMiddleInEnabledFormNotTextarea,
    !isInsideNonReadonlyForm && readonlyTextFieldMiddleNotInEnabledForm
  ]);

  const fieldRef = useRef<HTMLDivElement>(null);
  const ref = rootRef ?? fieldRef;
  const { tooltipContent, tooltipEventHandlerProps } = useTextFieldTooltip({
    hasTruncatedLabel,
    fieldRef: ref as RefObject<HTMLElement>,
    isDisabled: !hasInsideLabel,
    label: labelText
  });

  return (
    <div role="presentation" class={readonlyClassNames} ref={ref} {...tooltipEventHandlerProps}>
      {startContent && (
        <span class={readonlyTextFieldStartContentStyles}>
          <Flex justify="center" align="center" hasZeroMargins={true}>
            {startContent}
          </Flex>
        </span>
      )}
      <div class={middleClasses}>
        {insideLabel}
        {mainContent}
      </div>
      {endContent && (
        <span class={textFieldEndContent}>
          <Flex justify="center" align="center" hasZeroMargins={true}>
            {endContent}
          </Flex>
        </span>
      )}
      {tooltipContent}
    </div>
  );
};

export type { VariantProps };
